/**
 * Enhanced MCP Service with ContextManager Integration
 * Provides unified interface for MCP server management, tool execution,
 * and real-time context synchronization
 */

import axiosClient, { mcpClient } from '../api/axios.client';
import { getLangGraphBaseUrl } from '../config/brand.js';
import { getLangGraphWsUrl } from '../utils/langgraphConfig.js';
import ContextManager from '../models/ContextManager';
import MCP_API from '../api/mcp.api';

class MCPService {
  constructor() {
    this.connections = new Map();
    this.servers = [];
    this.wsConnected = false;
    this.userSettings = null;
    this.isAuthenticated = false;

    // Enhanced with ContextManager integration
    this.contextManager = new ContextManager();
    this.eventSubscriptions = new Map();
    this.activeStreams = new Map();
    this.toolCache = new Map(); // Cache for tool definitions
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
  }

  async initialize() {
    // Initialize the MCP service
    console.log('🔧 Initializing Enhanced MCP Service...');
    this.wsConnected = false;

    // Check authentication status
    this.isAuthenticated = this.checkAuthentication();
    console.log('🔧 Authentication status:', this.isAuthenticated);

    // Load servers from database or localStorage
    console.log('🔧 Loading servers...');
    await this.loadServers();
    console.log('🔧 Servers loaded:', this.servers.length, 'servers');

    // Initialize ContextManager with MCP integration
    await this.initializeContextManager();

    // Initialize WebSocket connection with enhanced features
    await this.initializeWebSocket();

    // Set up event listeners for real-time updates
    this.setupEventListeners();

    // Return this instance for compatibility with dashboard
    return this;
  }

  /**
   * Initialize ContextManager with MCP integration
   */
  async initializeContextManager() {
    try {
      // Set up context event listeners
      this.contextManager.subscribe('mcp:connected', (data) => {
        this.handleMCPServerConnected(data);
      });

      this.contextManager.subscribe('mcp:disconnected', (data) => {
        this.handleMCPServerDisconnected(data);
      });

      this.contextManager.subscribe('mcp:tool_executed', (data) => {
        this.handleMCPToolExecuted(data);
      });

      this.contextManager.subscribe('mcp:sync_completed', (data) => {
        this.handleMCPSyncCompleted(data);
      });

      console.log('✅ ContextManager initialized with MCP integration');
    } catch (error) {
      console.error('Failed to initialize ContextManager:', error);
    }
  }

  /**
   * Initialize WebSocket with enhanced features
   */
  async initializeWebSocket() {
    try {
      // Set up WebSocket event handlers BEFORE connecting
      MCP_API.wsClient.on('connected', (data) => {
        console.log('🔗 MCP Service detected WebSocket connected event');
        this.websocket = MCP_API.wsClient.ws;
        this.connected = true;
        this.notifyConnectionChange();
      });

      MCP_API.wsClient.on('disconnected', (data) => {
        console.log('🔌 MCP Service detected WebSocket disconnected event');
        this.websocket = null;
        this.connected = false;
        this.notifyConnectionChange();
      });

      MCP_API.wsClient.on('context:changed', (data) => {
        this.handleContextChanged(data);
      });

      MCP_API.wsClient.on('flow:execution', (data) => {
        this.handleFlowExecutionUpdate(data);
      });

      MCP_API.wsClient.on('tool:usage', (data) => {
        this.handleToolUsage(data);
      });

      // Connect to MCP WebSocket
      await MCP_API.connectWebSocket();

      // After connection attempt, check if we're actually connected
      if (MCP_API.isWebSocketConnected()) {
        console.log('🔗 WebSocket connection confirmed after initialization');
        this.websocket = MCP_API.wsClient.ws;
        this.connected = true;
        this.notifyConnectionChange();
      }

      console.log('✅ WebSocket initialized with enhanced features');
    } catch (error) {
      console.error('Failed to initialize WebSocket:', error);
    }
  }

  /**
   * Set up event listeners for real-time updates
   */
  setupEventListeners() {
    // Listen for connection status changes
    this.addConnectionListener((connected) => {
      this.wsConnected = connected;
      this.handleConnectionStatusChanged(connected);
    });

    console.log('✅ Event listeners set up');
  }

  checkAuthentication() {
    const token = localStorage.getItem('tkn');
    const user = localStorage.getItem('user');
    return !!(token && user);
  }

  // Migrate servers from localStorage to database when user logs in
  async migrateServersFromLocalStorage() {
    try {
      const savedServers = localStorage.getItem('mcpServers');
      if (savedServers && this.isAuthenticated) {
        const localServers = JSON.parse(savedServers);
        if (localServers.length > 0) {
          console.log('🔄 Migrating servers from localStorage to database:', localServers);

          // Save to database
          await this.saveServers();

          // Clear localStorage after successful migration
          localStorage.removeItem('mcpServers');
          console.log('✅ Migration complete, localStorage cleared');
        }
      }
    } catch (error) {
      console.error('Error migrating servers from localStorage:', error);
    }
  }

  async connectWebSocket() {
    try {
      // Connect to the MCP backend server WebSocket (port 8001 in dev, same-origin /langgraph in prod)
      const baseUrl = getLangGraphBaseUrl();
      const wsUrl = `${baseUrl.replace(/^http/, 'ws')}/api/v1/mcp/ws`;
      console.log('🔗 Attempting to connect to MCP backend WebSocket:', wsUrl);
      this.websocket = new WebSocket(wsUrl);

      this.websocket.onopen = () => {
        console.log('🔗 MCP WebSocket connected');
        this.wsConnected = true;
        // Notify any listeners about connection status change
        this.notifyConnectionChange();
      };

      this.websocket.onclose = () => {
        console.log('🔌 MCP WebSocket disconnected');
        this.wsConnected = false;
        // Notify any listeners about connection status change
        this.notifyConnectionChange();
      };

      this.websocket.onerror = (error) => {
        console.error('❌ MCP WebSocket error:', error);
        this.wsConnected = false;
        // Notify any listeners about connection status change
        this.notifyConnectionChange();

        // Try fallback to standalone MCP server
        console.log('🔄 Attempting fallback to standalone MCP server...');
        try {
          const baseUrl = getLangGraphBaseUrl();
          const fallbackWsUrl = `${baseUrl.replace(/^http/, 'ws')}/api/v1/mcp/ws`;
          console.log('🔗 Attempting fallback connection to:', fallbackWsUrl);
          this.websocket = new WebSocket(fallbackWsUrl);

          this.websocket.onopen = () => {
            console.log('✅ Fallback WebSocket connected to standalone server');
            this.wsConnected = true;
            this.notifyConnectionChange();
          };

          this.websocket.onerror = (fallbackError) => {
            console.error('❌ Fallback WebSocket connection also failed:', fallbackError);
            this.wsConnected = false;
            this.notifyConnectionChange();
          };

          this.websocket.onclose = () => {
            console.log('🔌 Fallback WebSocket disconnected');
            this.wsConnected = false;
            this.notifyConnectionChange();
          };

          this.websocket.onmessage = (event) => {
            this.handleMessage(JSON.parse(event.data));
          };
        } catch (fallbackError) {
          console.error('❌ Fallback WebSocket setup failed:', fallbackError);
          this.wsConnected = false;
          this.notifyConnectionChange();
        }
      };
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
      this.wsConnected = false;
      this.notifyConnectionChange();
    }
  }

  // Add connection status listeners
  addConnectionListener(callback) {
    if (!this.connectionListeners) {
      this.connectionListeners = [];
    }
    this.connectionListeners.push(callback);
  }

  removeConnectionListener(callback) {
    if (this.connectionListeners) {
      this.connectionListeners = this.connectionListeners.filter((cb) => cb !== callback);
    }
  }

  notifyConnectionChange() {
    if (this.connectionListeners) {
      this.connectionListeners.forEach((callback) => {
        try {
          callback(this.wsConnected);
        } catch (error) {
          console.error('Error in connection listener:', error);
        }
      });
    }
  }

  disconnectWebSocket() {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
      this.wsConnected = false;
    }
  }

  async loadServers() {
    try {
      console.log('🔍 Loading MCP servers. Authenticated:', this.isAuthenticated);
      console.log('🔍 Current servers count:', this.servers.length);

      // Always add the production MCP server as default
      const isProduction = typeof window !== 'undefined' && window.location
                          && window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1';

      const localMCPServer = {
        id: isProduction ? 'production-mcp-server' : 'local-mcp-server',
        name: isProduction ? 'Production MCP Server' : 'Local MCP Server',
        description: isProduction ? 'Production MCP server integrated with main backend' : 'Local development MCP server',
        host: isProduction ? window.location.hostname : 'localhost',
        port: isProduction ? 443 : 8765,
        protocol: isProduction ? 'https' : 'http',
        enabled: true,
        base_url: isProduction ? getLangGraphBaseUrl() : 'http://localhost:8765',
        health_endpoint: isProduction ? '/api/v1/mcp/status' : '/health',
        execute_endpoint: isProduction ? '/api/v1/mcp/tools/execute' : '/tools/execute',
        websocket_url: isProduction ? getLangGraphWsUrl('/api/v1/mcp/ws') : 'ws://localhost:8765/ws',
        tools: [
          {
            name: 'database_query',
            description: 'Execute database queries',
            parameters: {
              query: { type: 'string', description: 'SQL query to execute' },
              parameters: { type: 'array', description: 'Query parameters', optional: true },
            },
          },
          {
            name: 'file_operations',
            description: 'Perform file operations',
            parameters: {
              operation: { type: 'string', description: 'Operation type (read, write, list)' },
              path: { type: 'string', description: 'File path' },
              content: { type: 'string', description: 'File content', optional: true },
            },
          },
          {
            name: 'ai_processor',
            description: 'Process data with AI',
            parameters: {
              task: { type: 'string', description: 'AI task to perform' },
              data: { type: 'string', description: 'Data to process' },
            },
          },
          {
            name: 'http_request',
            description: 'Make HTTP requests',
            parameters: {
              url: { type: 'string', description: 'URL to request' },
              method: { type: 'string', description: 'HTTP method', optional: true },
              headers: { type: 'object', description: 'HTTP headers', optional: true },
              body: { type: 'string', description: 'Request body', optional: true },
            },
          },
        ],
      };

      // Add example external MCP servers for demonstration
      const exampleExternalServers = [
        {
          id: 'weather-mcp-server',
          name: 'Weather API MCP Server',
          description: 'Get weather information from OpenWeatherMap API',
          host: 'api.openweathermap.org',
          port: 80,
          protocol: 'https',
          enabled: false,
          base_url: 'https://api.openweathermap.org',
          health_endpoint: '/data/2.5/weather?q=London&appid=demo',
          execute_endpoint: '/data/2.5/weather',
          authToken: null, // Will be set when user provides API key
          websocket_url: null,
          tools: [
            {
              name: 'get_weather',
              description: 'Get current weather for a city',
              parameters: {
                city: { type: 'string', description: 'City name' },
                country: { type: 'string', description: 'Country code', optional: true },
                units: { type: 'string', description: 'Units (metric, imperial, kelvin)', optional: true },
              },
            },
            {
              name: 'get_forecast',
              description: 'Get weather forecast for a city',
              parameters: {
                city: { type: 'string', description: 'City name' },
                country: { type: 'string', description: 'Country code', optional: true },
                days: { type: 'number', description: 'Number of days (1-5)', optional: true },
              },
            },
          ],
        },
        {
          id: 'github-mcp-server',
          name: 'GitHub MCP Server',
          description: 'Access GitHub API for repository information',
          host: 'api.github.com',
          port: 443,
          protocol: 'https',
          enabled: false,
          base_url: 'https://api.github.com',
          health_endpoint: '/zen',
          execute_endpoint: '/repos',
          authToken: 'YOUR_GITHUB_TOKEN', // Will be set when user provides API key
          websocket_url: null,
          tools: [
            {
              name: 'get_user_info',
              description: 'Get GitHub user information',
              parameters: {
                username: { type: 'string', description: 'GitHub username' },
              },
            },
            {
              name: 'get_repo_info',
              description: 'Get repository information',
              parameters: {
                owner: { type: 'string', description: 'Repository owner' },
                repo: { type: 'string', description: 'Repository name' },
              },
            },
            {
              name: 'list_repos',
              description: 'List repositories for a user or organization',
              parameters: {
                username: { type: 'string', description: 'Username or organization', optional: true },
                type: { type: 'string', description: 'Type (owner, member, public)', optional: true },
              },
            },
          ],
        },
        {
          id: 'news-mcp-server',
          name: 'News API MCP Server',
          description: 'Get news articles from NewsAPI',
          host: 'newsapi.org',
          port: 443,
          protocol: 'https',
          enabled: false,
          base_url: 'https://newsapi.org',
          health_endpoint: '/v2/everything?q=bitcoin&apiKey=demo',
          execute_endpoint: '/v2/everything',
          authToken: 'YOUR_NEWSAPI_KEY', // Will be set when user provides API key
          websocket_url: null,
          tools: [
            {
              name: 'get_news',
              description: 'Get news articles by query',
              parameters: {
                query: { type: 'string', description: 'Search query' },
                language: { type: 'string', description: 'Language code', optional: true },
                country: { type: 'string', description: 'Country code', optional: true },
                category: { type: 'string', description: 'News category', optional: true },
              },
            },
            {
              name: 'get_top_headlines',
              description: 'Get top headlines',
              parameters: {
                country: { type: 'string', description: 'Country code', optional: true },
                category: { type: 'string', description: 'News category', optional: true },
                sources: { type: 'string', description: 'Comma-separated source IDs', optional: true },
              },
            },
          ],
        },
        {
          // === TEMPLATE: Add new MCP servers here ===
          // Replace this template with your actual server configuration
          id: 'your-new-server',
          name: 'Your New MCP Server',
          description: 'Description of what your server does',
          host: 'api.example.com',
          port: 443,
          protocol: 'https',
          enabled: false,
          base_url: 'https://api.example.com',
          health_endpoint: '/health', // Health check endpoint
          execute_endpoint: '/execute', // Main execution endpoint
          authToken: null, // Set to 'YOUR_API_KEY' if needed, or null for no auth
          websocket_url: null, // WebSocket URL if available
          tools: [
            {
              name: 'your_tool_name',
              description: 'Description of what this tool does',
              parameters: {
                param1: { type: 'string', description: 'Parameter description' },
                param2: { type: 'number', description: 'Optional parameter', optional: true },
              },
            },
          ],
        },
      ];

      // Add a working demo server that doesn't require API keys
      const demoServers = [
        {
          id: 'httpbin-mcp-server',
          name: 'HTTPBin MCP Server',
          description: 'Test HTTP requests with httpbin.org (no API key required)',
          host: 'httpbin.org',
          port: 443,
          protocol: 'https',
          enabled: false,
          base_url: 'https://httpbin.org',
          health_endpoint: '/status/200',
          execute_endpoint: '/anything',
          websocket_url: null,
          tools: [
            {
              name: 'test_get_request',
              description: 'Test GET request to httpbin',
              parameters: {
                path: { type: 'string', description: 'API endpoint path', optional: true },
                headers: { type: 'object', description: 'HTTP headers', optional: true },
              },
            },
            {
              name: 'test_post_request',
              description: 'Test POST request to httpbin',
              parameters: {
                path: { type: 'string', description: 'API endpoint path', optional: true },
                data: { type: 'object', description: 'Data to send', optional: true },
                headers: { type: 'object', description: 'HTTP headers', optional: true },
              },
            },
            {
              name: 'get_ip_info',
              description: 'Get your IP address and location info',
              parameters: {},
            },
          ],
        },
        {
          id: 'jsonplaceholder-mcp-server',
          name: 'JSONPlaceholder MCP Server',
          description: 'Test REST API with jsonplaceholder.typicode.com (no API key required)',
          host: 'jsonplaceholder.typicode.com',
          port: 443,
          protocol: 'https',
          enabled: false,
          base_url: 'https://jsonplaceholder.typicode.com',
          health_endpoint: '/todos/1',
          execute_endpoint: '/todos',
          websocket_url: null,
          tools: [
            {
              name: 'get_todos',
              description: 'Get list of todos',
              parameters: {
                limit: { type: 'number', description: 'Number of todos to fetch', optional: true },
              },
            },
            {
              name: 'create_todo',
              description: 'Create a new todo item',
              parameters: {
                title: { type: 'string', description: 'Todo title' },
                completed: { type: 'boolean', description: 'Completion status', optional: true },
              },
            },
            {
              name: 'get_users',
              description: 'Get list of users',
              parameters: {
                limit: { type: 'number', description: 'Number of users to fetch', optional: true },
              },
            },
          ],
        },
      ];

      // Check if local MCP server already exists
      const existingIndex = this.servers.findIndex((s) => s.id === 'local-mcp-server');
      if (existingIndex === -1) {
        console.log('🔧 Adding local MCP server to servers list');
        this.servers.push(localMCPServer);
        console.log('✅ Local MCP server added. Total servers:', this.servers.length);
      }

      // Add example external MCP servers for demonstration (disabled by default)
      exampleExternalServers.forEach((exampleServer) => {
        const existingExampleIndex = this.servers.findIndex((s) => s.id === exampleServer.id);
        if (existingExampleIndex === -1) {
          console.log('🔧 Adding example MCP server:', exampleServer.name);
          this.servers.push(exampleServer);
        }
      });

      // Add demo servers that work without API keys
      demoServers.forEach((demoServer) => {
        const existingDemoIndex = this.servers.findIndex((s) => s.id === demoServer.id);
        if (existingDemoIndex === -1) {
          console.log('🔧 Adding demo MCP server:', demoServer.name);
          this.servers.push(demoServer);
        }
      });

      if (this.isAuthenticated) {
        // Load from database via user settings
        try {
          const response = await axiosClient.get('users/settings');
          this.userSettings = response.settings;
          const dbServers = response.settings?.mcpSettings?.servers || [];

          // Merge database servers with local server (don't overwrite)
          dbServers.forEach((dbServer) => {
            const existingDbIndex = this.servers.findIndex((s) => s.id === dbServer.id);
            if (existingDbIndex === -1) {
              this.servers.push(dbServer);
            }
          });
          console.log('✅ MCP servers loaded from database:', this.servers.length, 'servers');

          // Check if we have servers in localStorage that need migration
          const savedServers = localStorage.getItem('mcpServers');
          if (savedServers) {
            const localServers = JSON.parse(savedServers);
            if (localServers.length > 0 && this.servers.length === 0) {
              console.log('🔄 Found servers in localStorage, migrating to database...');
              this.servers = localServers;
              await this.saveServers();
            }
            // Clear localStorage after successful database load
            localStorage.removeItem('mcpServers');
            console.log('🧹 Cleared localStorage to prevent conflicts');
          }
          return;
        } catch (error) {
          console.log('❌ Database load failed, falling back to localStorage:', error.message);
        }
      }

      // Fallback to localStorage
      const savedServers = localStorage.getItem('mcpServers');
      this.servers = savedServers ? JSON.parse(savedServers) : [];
      console.log('📱 MCP servers loaded from localStorage:', this.servers.length, 'servers');
    } catch (error) {
      console.error('Error loading MCP servers:', error);

      // Fallback to localStorage
      try {
        const savedServers = localStorage.getItem('mcpServers');
        this.servers = savedServers ? JSON.parse(savedServers) : [];
        console.log('📱 Fallback to localStorage:', this.servers.length, 'servers');
      } catch (localError) {
        console.error('Error loading from localStorage:', localError);
        this.servers = [];
      }
    }
  }

  async saveServers() {
    try {
      console.log('💾 Saving MCP servers. Authenticated:', this.isAuthenticated, 'Servers count:', this.servers.length);

      if (this.isAuthenticated) {
        // Save to database via user settings
        try {
          const payload = {
            mcpSettings: {
              servers: this.servers,
              selectedServer: this.userSettings?.mcpSettings?.selectedServer || null,
              selectedTool: this.userSettings?.mcpSettings?.selectedTool || null,
            },
          };
          console.log('📤 Sending to database:', JSON.stringify(payload, null, 2));

          await axiosClient.patch('users/settings', payload);
          console.log('✅ MCP servers saved to database');
          return;
        } catch (error) {
          console.log('❌ Database save failed, falling back to localStorage:', error.message);
          console.error('Full error:', error);
        }
      }

      // Fallback to localStorage
      localStorage.setItem('mcpServers', JSON.stringify(this.servers));
      console.log('📱 MCP servers saved to localStorage');
    } catch (error) {
      console.error('Error saving MCP servers:', error);

      // Fallback to localStorage
      try {
        localStorage.setItem('mcpServers', JSON.stringify(this.servers));
      } catch (localError) {
        console.error('Error saving to localStorage:', localError);
      }
    }
  }

  async addServer(server) {
    console.log('➕ Adding MCP server:', server);

    // Check if server already exists
    const existingIndex = this.servers.findIndex((s) => s.id === server.id);
    if (existingIndex !== -1) {
      console.log('🔧 Server already exists, updating instead of adding:', server.id);
      this.servers[existingIndex] = server;
    } else {
      console.log('🆕 Adding new server to array. Current servers:', this.servers.length);
      this.servers.push(server);
      console.log('✅ Server added. New servers count:', this.servers.length);
    }

    console.log('💾 Calling saveServers...');
    await this.saveServers();
    console.log('✅ addServer completed');
  }

  async updateServer(serverId, updates) {
    const index = this.servers.findIndex((s) => s.id === serverId);
    if (index !== -1) {
      this.servers[index] = { ...this.servers[index], ...updates };
      await this.saveServers();
    }
  }

  async removeServer(serverId) {
    this.servers = this.servers.filter((s) => s.id !== serverId);
    await this.saveServers();
  }

  getServer(serverId) {
    return this.servers.find((s) => s.id === serverId);
  }

  getAllServers() {
    return this.servers;
  }

  // Utility method to clear localStorage and force reload from database
  async clearLocalStorageAndReload() {
    console.log('🧹 Clearing localStorage and reloading from database...');
    localStorage.removeItem('mcpServers');
    await this.loadServers();
    return this.servers;
  }

  // Utility method to clear all servers and start fresh
  async clearAllServers() {
    console.log('🧹 Clearing all servers...');
    this.servers = [];
    await this.saveServers();
    localStorage.removeItem('mcpServers');
    return this.servers;
  }

  /**
   * Event Handlers
   */

  handleMCPServerConnected(data) {
    console.log('🟢 MCP Server connected:', data.serverId);
    this.emit('server:connected', data);
  }

  handleMCPServerDisconnected(data) {
    console.log('🔴 MCP Server disconnected:', data.serverId);
    this.emit('server:disconnected', data);
  }

  handleMCPToolExecuted(data) {
    console.log('⚡ MCP Tool executed:', data.toolName);
    this.emit('tool:executed', data);
  }

  handleMCPSyncCompleted(data) {
    console.log('🔄 MCP Sync completed:', data.operationId);
    this.emit('sync:completed', data);
  }

  handleContextChanged(data) {
    console.log('📝 Context changed:', data);
    this.emit('context:changed', data);
  }

  handleFlowExecutionUpdate(data) {
    console.log('🔄 Flow execution update:', data);
    this.emit('flow:execution', data);
  }

  handleToolUsage(data) {
    console.log('📊 Tool usage:', data);
    this.emit('tool:usage', data);
  }

  handleConnectionStatusChanged(connected) {
    console.log(`🌐 MCP Connection status changed: ${connected ? 'connected' : 'disconnected'}`);
    this.emit('connection:changed', { connected });
  }

  /**
   * Event System
   */
  addEventListener(event, handler) {
    if (!this.eventSubscriptions.has(event)) {
      this.eventSubscriptions.set(event, new Set());
    }
    this.eventSubscriptions.get(event).add(handler);
  }

  removeEventListener(event, handler) {
    if (this.eventSubscriptions.has(event)) {
      this.eventSubscriptions.get(event).delete(handler);
    }
  }

  emit(event, data) {
    if (this.eventSubscriptions.has(event)) {
      this.eventSubscriptions.get(event).forEach((handler) => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Enhanced MCP Methods
   */

  /**
   * Connect to MCP server with context integration
   */
  async connectToMCPServer(serverConfig) {
    try {
      // Connect via ContextManager
      await this.contextManager.connectToMCPServer(serverConfig);

      // Connect via WebSocket
      if (serverConfig.host && serverConfig.port) {
        await this.connectToServer(serverConfig.id);
      }

      console.log(`✅ Connected to MCP server: ${serverConfig.name}`);
      return true;
    } catch (error) {
      console.error('Failed to connect to MCP server:', error);
      throw error;
    }
  }

  /**
   * Execute tool with context tracking
   */
  async executeToolWithContext(serverId, toolName, args) {
    try {
      // Execute via ContextManager
      const executionContext = await this.contextManager.executeMCPTool(serverId, toolName, args);

      // Execute via WebSocket (if connected)
      if (this.wsConnected) {
        const result = await MCP_API.executeTool({
          tool_name: toolName,
          arguments: args,
          server_id: serverId,
        });

        // Update context with result
        this.contextManager.set(
          `tool_result_${Date.now()}`,
          result,
          'mcp',
          serverId,
          { toolName, executionId: executionContext.timestamp },
        );

        return result;
      }
      // Fallback to HTTP execution
      const result = await this.executeTool(serverId, toolName, args);
      return result;
    } catch (error) {
      console.error('Failed to execute tool with context:', error);
      throw error;
    }
  }

  /**
   * Stream context changes with automatic subscription
   */
  async streamContext(scope, onChange) {
    const streamId = `context_stream_${Date.now()}`;

    // Subscribe to context changes via WebSocket
    const subscription = await MCP_API.streamContextChanges(scope, onChange);

    // Store active stream
    this.activeStreams.set(streamId, {
      type: 'context',
      scope,
      subscription,
      onChange,
    });

    return {
      streamId,
      stop: async () => {
        await subscription.unsubscribe();
        this.activeStreams.delete(streamId);
      },
    };
  }

  /**
   * Stream flow execution with progress tracking
   */
  async streamFlowExecution(flowId, onUpdate) {
    const streamId = `flow_stream_${Date.now()}`;

    // Subscribe to flow execution updates
    const subscription = await MCP_API.streamFlowExecution(flowId, onUpdate);

    // Store active stream
    this.activeStreams.set(streamId, {
      type: 'flow',
      flowId,
      subscription,
      onUpdate,
    });

    return {
      streamId,
      stop: async () => {
        await subscription.unsubscribe();
        this.activeStreams.delete(streamId);
      },
    };
  }

  /**
   * Get unified context statistics
   */
  getUnifiedStats() {
    return {
      context: this.contextManager.getContextStats(),
      connections: {
        websocket: this.wsConnected,
        servers: this.servers.length,
        activeConnections: this.connections.size,
      },
      streams: {
        active: this.activeStreams.size,
        subscriptions: MCP_API.getActiveSubscriptions().length,
      },
      performance: {
        toolCache: this.toolCache.size,
        eventSubscriptions: this.eventSubscriptions.size,
      },
    };
  }

  /**
   * Sync context across all connected MCP servers
   */
  async syncAllContexts() {
    try {
      const syncPromises = [];

      // Sync via ContextManager
      syncPromises.push(this.contextManager.syncWithMCPServer('all'));

      // Sync via WebSocket (if connected)
      if (this.wsConnected) {
        syncPromises.push(MCP_API.sendWebSocketRequest('sync_context', {
          timestamp: new Date().toISOString(),
        }));
      }

      await Promise.all(syncPromises);
      console.log('✅ All contexts synchronized');
    } catch (error) {
      console.error('Failed to sync all contexts:', error);
      throw error;
    }
  }

  /**
   * Get available tools with caching
   */
  async getAvailableToolsWithCache(serverId) {
    const cacheKey = `tools_${serverId}`;

    // Check cache first
    if (this.toolCache.has(cacheKey)) {
      const cached = this.toolCache.get(cacheKey);
      if (Date.now() - cached.timestamp < this.cacheTimeout) {
        return cached.tools;
      }
    }

    // Fetch fresh tools
    const tools = await this.getAvailableTools(serverId);

    // Cache the result
    this.toolCache.set(cacheKey, {
      tools,
      timestamp: Date.now(),
    });

    return tools;
  }

  /**
   * Enhanced Backend Integration Methods
   */

  /**
   * Execute tool with enhanced context via backend API
   */
  async executeToolWithEnhancedContext(serverId, toolName, args) {
    try {
      const response = await mcpClient.post('/tools/execute-with-context', {
        tool_name: toolName,
        arguments: args,
        context: args.context,
        options: {
          server_id: serverId,
          flow_id: args.flowId,
          node_id: args.nodeId,
        },
      });

      return response.data;
    } catch (error) {
      console.error('Backend tool execution failed:', error);
      // Fallback to local execution
      return await this.executeToolWithContext(serverId, toolName, args);
    }
  }

  /**
   * Sync context with backend
   */
  async syncContextWithBackend(contextData, options = {}) {
    try {
      const response = await mcpClient.post('/context/sync', {
        context: contextData,
        options: {
          ...options,
          connection_id: options.connectionId || 'frontend',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Backend context sync failed:', error);
      throw error;
    }
  }

  /**
   * Broadcast context update to all connected clients
   */
  async broadcastContextUpdate(scope, key, value, metadata = {}) {
    try {
      const response = await mcpClient.post('/context/broadcast', {
        scope,
        key,
        value,
        metadata: {
          ...metadata,
          source: 'frontend',
          timestamp: new Date().toISOString(),
        },
      });

      return response.data;
    } catch (error) {
      console.error('Context broadcast failed:', error);
      throw error;
    }
  }

  /**
   * Subscribe to backend event streams
   */
  async subscribeToBackendEvent(eventType, options = {}) {
    try {
      const response = await mcpClient.post('/events/subscribe', {
        event_type: eventType,
        options,
        connection_id: options.connectionId || 'frontend',
      });

      if (response.data.success) {
        return {
          subscriptionId: response.data.subscription.id,
          eventType,
          options,
          unsubscribe: async () => {
            await mcpClient.post('/events/unsubscribe', {
              subscription_id: response.data.subscription.id,
              connection_id: options.connectionId || 'frontend',
            });
          },
        };
      }

      throw new Error('Subscription failed');
    } catch (error) {
      console.error('Backend event subscription failed:', error);
      throw error;
    }
  }

  /**
   * Get enhanced statistics from backend
   */
  async getBackendStats() {
    try {
      const [subscriptionsRes, eventsRes, healthRes] = await Promise.all([
        mcpClient.get('/context/subscriptions'),
        mcpClient.get('/events/active'),
        mcpClient.get('/health'),
      ]);

      return {
        subscriptions: subscriptionsRes.data,
        events: eventsRes.data,
        health: healthRes.data,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to get backend stats:', error);
      return null;
    }
  }

  /**
   * Get comprehensive system metrics
   */
  async getSystemMetrics() {
    try {
      const [metricsRes, aiStatsRes, storageRes] = await Promise.all([
        mcpClient.get('/metrics'),
        mcpClient.get('/ai/stats'),
        mcpClient.get('/storage/stats'),
      ]);

      return {
        mcp: metricsRes.data,
        ai: aiStatsRes.data,
        storage: storageRes.data,
        timestamp: new Date().toISOString(),
      };
    } catch (error) {
      console.error('Failed to get system metrics:', error);
      return null;
    }
  }

  /**
   * Enhanced flow execution with real-time streaming
   */
  async executeFlowWithStreaming(flowId, input, onProgress) {
    try {
      // Subscribe to flow execution events
      const streamSubscription = await this.streamFlowExecution(flowId, (data) => {
        onProgress?.(data);
      });

      // Execute flow via backend with enhanced context
      const result = await this.executeToolWithEnhancedContext('flow', `execute_flow_${flowId}`, {
        input,
        flowId,
        context: {
          global: this.contextManager.getAll('global'),
          flow: this.contextManager.getAll('flow'),
          mcp: this.contextManager.getAll('mcp'),
        },
      });

      // Clean up stream after execution
      setTimeout(() => {
        streamSubscription.stop();
      }, 5000); // Clean up after 5 seconds

      return result;
    } catch (error) {
      console.error('Flow execution with streaming failed:', error);
      throw error;
    }
  }

  /**
   * Get real-time performance dashboard
   */
  async getPerformanceDashboard() {
    try {
      const [unifiedStats, backendStats, systemMetrics] = await Promise.all([
        this.getUnifiedStats(),
        this.getBackendStats(),
        this.getSystemMetrics(),
      ]);

      // Calculate performance indicators
      const performance = {
        frontend: unifiedStats,
        backend: backendStats,
        system: systemMetrics,
        analysis: this.analyzePerformance(unifiedStats, backendStats, systemMetrics),
        recommendations: this.getPerformanceRecommendations(unifiedStats, backendStats, systemMetrics),
        timestamp: new Date().toISOString(),
      };

      return performance;
    } catch (error) {
      console.error('Failed to get performance dashboard:', error);
      return null;
    }
  }

  /**
   * Analyze performance metrics and identify issues
   */
  analyzePerformance(frontend, backend, system) {
    const analysis = {
      issues: [],
      warnings: [],
      good: [],
      scores: {
        overall: 100,
        contextSync: 100,
        websocket: 100,
        memory: 100,
        network: 100,
      },
    };

    // Context synchronization analysis
    if (frontend?.context?.mcp?.syncQueue > 10) {
      analysis.issues.push('High context sync queue - potential backlog');
      analysis.scores.contextSync = Math.max(0, 100 - (frontend.context.mcp.syncQueue * 5));
    }

    // WebSocket connection analysis
    if (!frontend?.connections?.websocket) {
      analysis.issues.push('WebSocket connection lost');
      analysis.scores.websocket = 0;
    } else if (frontend?.connections?.activeConnections > 50) {
      analysis.warnings.push('High number of active connections');
      analysis.scores.websocket = 80;
    } else {
      analysis.good.push('WebSocket connections healthy');
    }

    // Memory usage analysis
    const memoryUsage = system?.ai?.memory_usage_mb || 0;
    if (memoryUsage > 500) {
      analysis.issues.push('High memory usage detected');
      analysis.scores.memory = Math.max(0, 100 - ((memoryUsage - 500) / 10));
    } else if (memoryUsage > 300) {
      analysis.warnings.push('Memory usage above normal range');
      analysis.scores.memory = 85;
    } else {
      analysis.good.push('Memory usage optimal');
    }

    // Network latency analysis
    const latency = frontend?.performance?.averageLatency || 0;
    if (latency > 200) {
      analysis.issues.push('High network latency detected');
      analysis.scores.network = Math.max(0, 100 - ((latency - 200) / 5));
    } else if (latency > 100) {
      analysis.warnings.push('Network latency above optimal');
      analysis.scores.network = 90;
    } else {
      analysis.good.push('Network latency optimal');
    }

    // Calculate overall score
    analysis.scores.overall = Math.round(
      (analysis.scores.contextSync + analysis.scores.websocket
       + analysis.scores.memory + analysis.scores.network) / 4,
    );

    return analysis;
  }

  /**
   * Get performance optimization recommendations
   */
  getPerformanceRecommendations(frontend, backend, system) {
    const recommendations = [];

    // Context sync recommendations
    if (frontend?.context?.mcp?.syncQueue > 20) {
      recommendations.push({
        priority: 'high',
        category: 'context',
        title: 'Reduce Context Sync Frequency',
        description: 'High sync queue detected. Consider increasing sync interval or optimizing context updates.',
        action: 'Update syncConfig.syncInterval in ContextManager',
      });
    }

    // Memory recommendations
    const memoryUsage = system?.ai?.memory_usage_mb || 0;
    if (memoryUsage > 400) {
      recommendations.push({
        priority: 'high',
        category: 'memory',
        title: 'Optimize Memory Usage',
        description: 'Memory usage is high. Consider implementing cache cleanup and memory optimization.',
        action: 'Run cleanup() method and optimize cache TTL',
      });
    }

    // Connection recommendations
    if (frontend?.connections?.activeConnections > 30) {
      recommendations.push({
        priority: 'medium',
        category: 'network',
        title: 'Monitor Connection Pool',
        description: 'High number of active connections. Monitor for connection leaks.',
        action: 'Check connection cleanup in WebSocket manager',
      });
    }

    // Tool cache recommendations
    if (frontend?.performance?.toolCache > 100) {
      recommendations.push({
        priority: 'low',
        category: 'cache',
        title: 'Optimize Tool Cache',
        description: 'Tool cache is growing large. Consider cache size limits.',
        action: 'Implement cache size limits in MCPService',
      });
    }

    return recommendations;
  }

  /**
   * Performance optimization methods
   */
  async optimizePerformance() {
    const optimizations = [];

    try {
      // Optimize context sync interval
      if (this.contextManager.syncConfig.syncInterval < 10000) {
        this.contextManager.syncConfig.syncInterval = 10000; // Increase to 10 seconds
        optimizations.push('Increased context sync interval to reduce load');
      }

      // Clear old caches
      const cleared = await this.clearOldCaches();
      if (cleared > 0) {
        optimizations.push(`Cleared ${cleared} old cache entries`);
      }

      // Optimize WebSocket settings
      if (this.contextManager.mcpConnectionState.reconnectAttempts > 5) {
        optimizations.push('WebSocket connection issues detected - check network');
      }

      console.log('⚡ Performance optimizations applied:', optimizations);
      return optimizations;
    } catch (error) {
      console.error('Performance optimization failed:', error);
      return [];
    }
  }

  /**
   * Clear old cache entries
   */
  async clearOldCaches() {
    let clearedCount = 0;

    // Clear old tool cache entries
    const cacheKeys = Array.from(this.toolCache.keys());
    const now = Date.now();

    for (const key of cacheKeys) {
      const cached = this.toolCache.get(key);
      if (now - cached.timestamp > this.cacheTimeout * 2) { // 2x normal timeout
        this.toolCache.delete(key);
        clearedCount++;
      }
    }

    // Clear old context history
    if (this.contextManager.history) {
      Object.keys(this.contextManager.history).forEach((scope) => {
        if (Array.isArray(this.contextManager.history[scope])) {
          const history = this.contextManager.history[scope];
          if (history.length > this.contextManager.maxHistoryLength) {
            this.contextManager.history[scope] = history.slice(0, this.contextManager.maxHistoryLength);
            clearedCount++;
          }
        }
      });
    }

    return clearedCount;
  }

  /**
   * Get system health status
   */
  async getSystemHealth() {
    try {
      const dashboard = await this.getPerformanceDashboard();
      const analysis = dashboard?.analysis;

      return {
        status: analysis?.scores?.overall >= 70 ? 'healthy' : 'warning',
        score: analysis?.scores?.overall || 0,
        issues: analysis?.issues?.length || 0,
        warnings: analysis?.warnings?.length || 0,
        recommendations: analysis?.recommendations?.length || 0,
        details: analysis,
      };
    } catch (error) {
      console.error('Failed to get system health:', error);
      return {
        status: 'error',
        score: 0,
        issues: 1,
        warnings: 0,
        recommendations: 0,
        details: { error: error.message },
      };
    }
  }

  /**
   * Clean up resources
   */
  async cleanup() {
    try {
      // Stop all active streams
      for (const [streamId, stream] of this.activeStreams) {
        await stream.stop();
      }
      this.activeStreams.clear();

      // Clear caches
      this.toolCache.clear();

      // Disconnect WebSocket
      await MCP_API.disconnectWebSocket();

      // Clean up backend subscriptions
      try {
        await mcpClient.post('/events/unsubscribe', {
          subscription_id: 'all',
          connection_id: 'frontend',
        });
      } catch (error) {
        console.warn('Backend cleanup warning:', error);
      }

      console.log('🧹 Enhanced MCP Service cleaned up');
    } catch (error) {
      console.error('Error during enhanced cleanup:', error);
    }
  }

  /**
   * MCP Client class for WebSocket connections
   */
  createClient(config) {
    return new MCPClient(config);
  }

  async connectToServer(serverId) {
    const server = this.servers.find((s) => s.id === serverId);
    if (!server) {
      throw new Error('Server not found');
    }

    if (this.connections.has(serverId)) {
      return this.connections.get(serverId);
    }

    try {
      const client = this.createClient({
        host: server.host,
        port: server.port,
      });

      await client.connect();
      this.connections.set(serverId, client);

      // Update server status
      this.servers = this.servers.map((s) => (s.id === serverId
        ? { ...s, lastConnected: new Date().toISOString(), connectionStatus: 'connected' }
        : s));
      this.saveServers();

      return client;
    } catch (error) {
      this.servers = this.servers.map((s) => (s.id === serverId
        ? { ...s, connectionStatus: 'error', lastError: error.message }
        : s));
      this.saveServers();
      throw error;
    }
  }

  async disconnectFromServer(serverId) {
    const client = this.connections.get(serverId);
    if (client) {
      await client.disconnect();
      this.connections.delete(serverId);

      this.servers = this.servers.map((s) => (s.id === serverId
        ? { ...s, connectionStatus: 'disconnected' }
        : s));
      this.saveServers();
    }
  }

  async executeTool(serverId, toolName, toolArguments = {}) {
    console.log('🔧 executeTool called for serverId:', serverId, 'toolName:', toolName);

    // Check if this is the standalone MCP server
    const server = this.servers.find((s) => s.id === serverId);
    console.log('🔧 Found server:', server);

    // Handle tool execution based on server type
    if (server) {
      if (server.base_url.includes('langgraph')) {
        // Production: use MCP API through WebSocket
        console.log('🔧 Executing tool via production MCP API');
        try {
          const result = await this.executeToolRealtime(serverId, {
            tool_name: toolName,
            parameters: toolArguments,
          });
          console.log('🔧 Tool execution result:', result);
          return result;
        } catch (error) {
          console.error('Failed to execute tool via production MCP API:', error);
          throw error;
        }
      } else if (server.host === 'localhost' && server.port === 8765) {
        // Development: use standalone MCP server directly
        console.log('🔧 Executing tool on standalone MCP server directly');
        try {
          const response = await fetch(`${server.base_url}/tools/execute`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              tool_name: toolName,
              parameters: toolArguments,
              context: {},
            }),
          });

          console.log('🔧 Direct execution response status:', response.status);

          if (response.ok) {
            const result = await response.json();
            console.log('🔧 Tool execution result:', result);
            return result;
          }
          const errorText = await response.text();
          console.error('🔧 Direct execution failed:', response.status, errorText);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        } catch (error) {
          console.error('🔧 Failed to execute tool on standalone MCP server:', error);
          throw error;
        }
      }
    }

    // Handle HTTP/HTTPS servers (external MCP servers)
    if (server && (server.protocol === 'https' || server.protocol === 'http') && server.host !== 'localhost') {
      console.log('🔧 Executing tool on external HTTP server:', server.host);
      try {
        // Build the URL with the execute_endpoint
        const endpoint = server.execute_endpoint || '/execute';
        const url = `${server.base_url}${endpoint}`;

        // Build request parameters
        const requestData = {
          tool_name: toolName,
          parameters: toolArguments,
          context: {},
        };

        // Add authentication if provided
        if (server.authToken) {
          requestData.auth_token = server.authToken;
        }

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(requestData),
        });

        console.log('🔧 External server response status:', response.status);

        if (response.ok) {
          const result = await response.json();
          console.log('🔧 External tool execution result:', result);
          return result;
        }
        const errorText = await response.text();
        console.error('🔧 External server execution failed:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      } catch (error) {
        console.error('🔧 Failed to execute tool on external server:', error);
        throw error;
      }
    }

    // For other servers, use the original WebSocket client approach
    console.log('🔧 Using WebSocket client for server:', serverId);
    const client = await this.connectToServer(serverId);
    return await client.executeTool(toolName, toolArguments);
  }

  async getAvailableTools(serverId) {
    console.log('🔧 getAvailableTools called for serverId:', serverId);

    // Check if this is the local MCP server
    const server = this.servers.find((s) => s.id === serverId);
    console.log('🔧 Found server:', server);

    if (server) {
      if (server.base_url.includes('langgraph')) {
        // Production: use MCP API through WebSocket
        console.log('🔧 Using production MCP API');

        // Check if WebSocket is connected via MCP_API
        console.log('🔧 Checking MCP_API WebSocket connection status');
        if (!MCP_API.isWebSocketConnected()) {
          console.log('🔧 MCP_API WebSocket not connected, attempting to connect...');
          try {
            await MCP_API.connectWebSocket();
            console.log('🔧 MCP_API connectWebSocket() completed');

            // Wait a moment for the connection to be fully established
            await new Promise((resolve) => setTimeout(resolve, 1000));

            if (!MCP_API.isWebSocketConnected()) {
              console.error('🔧 WebSocket connection not established properly');
              return [];
            }

            console.log('🔧 WebSocket connected, now fetching tools...');
          } catch (connectError) {
            console.error('Failed to establish WebSocket connection:', connectError);
            return [];
          }
        } else {
          console.log('🔧 WebSocket already connected via MCP_API');
        }

        // Update our internal state to match MCP_API
        this.websocket = MCP_API.wsClient.ws;
        this.connected = true;

        try {
          // Directly send tools/list request using MCP_API's websocket
          // Use a proper request ID instead of relying on this.requestId
          const requestId = Date.now().toString();
          const request = {
            jsonrpc: '2.0',
            id: requestId,
            method: 'tools/list',
            params: {},
          };

          console.log('🔧 Sending tools/list request with ID:', requestId);

          // Send request using MCP_API's websocket directly
          const result = await new Promise((resolve, reject) => {
            if (!MCP_API.wsClient.ws || MCP_API.wsClient.ws.readyState !== WebSocket.OPEN) {
              console.error('🔧 WebSocket not connected for tools request');
              reject(new Error('WebSocket not connected'));
              return;
            }

            console.log('🔧 Setting up pending request for ID:', request.id);
            MCP_API.wsClient.pendingRequests.set(request.id, { resolve, reject });
            MCP_API.wsClient.ws.send(JSON.stringify(request));
            console.log('🔧 Sent tools/list request');

            // Timeout after 30 seconds
            setTimeout(() => {
              if (MCP_API.wsClient.pendingRequests.has(request.id)) {
                console.log('🔧 Tools request timed out for ID:', request.id);
                MCP_API.wsClient.pendingRequests.delete(request.id);
                reject(new Error('Request timeout'));
              }
            }, 30000);
          });

          console.log('🔧 Received tools response:', result);
          const tools = result.tools || [];
          this.tools = tools;
          console.log('🔧 Returning tools from production MCP server:', tools);
          return tools;
        } catch (error) {
          console.error('Failed to fetch tools from production MCP server:', error);
          return [];
        }
      } else if (server.host === 'localhost' && server.port === 8765) {
        // Development: use standalone MCP server directly
        console.log('🔧 Using standalone MCP server directly');
        try {
          const response = await fetch(`${server.base_url}/tools`);
          if (response.ok) {
            const data = await response.json();
            const tools = data.tools || data.server?.available_tools || [];
            console.log('🔧 Returning tools from standalone MCP server:', tools);
            return tools;
          }
        } catch (error) {
          console.error('Failed to fetch tools from standalone MCP server:', error);
        }
      }
    }

    // Handle HTTP/HTTPS servers (external MCP servers)
    if (server && (server.protocol === 'https' || server.protocol === 'http') && server.host !== 'localhost') {
      console.log('🔧 Fetching tools from external HTTP server:', server.host);
      try {
        const response = await fetch(`${server.base_url}/api/v1/mcp/tools`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        });

        console.log('🔧 External server tools response status:', response.status);

        if (response.ok) {
          const data = await response.json();
          const tools = data.tools || data.server?.available_tools || [];
          console.log('🔧 Returning tools from external server:', tools);
          return tools;
        }
        console.error('🔧 External server tools request failed:', response.status, response.statusText);
        return [];
      } catch (error) {
        console.error('Failed to fetch tools from external server:', error);
        return [];
      }
    }

    // Fallback to backend API if needed
    if (server && server.host === 'localhost' && server.port === 8001) {
      console.log('🔧 Using backend API for local MCP server');
      try {
        const response = await mcpClient.get('/api/v1/mcp/tools');
        console.log('🔧 Backend API response:', response);
        if (response && Array.isArray(response)) {
          console.log('🔧 Returning tools from backend API:', response);
          return response;
        }
      } catch (error) {
        console.error('Failed to fetch tools from backend API:', error);
      }
    }

    // For external MCP servers, use WebSocket connection
    try {
      const client = await this.connectToServer(serverId);
      return await client.listTools();
    } catch (error) {
      console.error('Failed to connect to MCP server:', error);
      return [];
    }
  }

  async getAvailableResources(serverId) {
    const client = await this.connectToServer(serverId);
    return await client.listResources();
  }

  getConnectionStatus(serverId) {
    return this.connections.has(serverId) ? 'connected' : 'disconnected';
  }

  getServers() {
    return this.servers;
  }

  // Execute MCP tool from chat interface
  async executeMCPToolFromChat(serverId, toolName, userInput, conversationId) {
    try {
      // Prepare arguments for the tool
      const toolArguments = {
        input: { input: userInput },
        user_id: localStorage.getItem('username') || 'anonymous',
        session_id: conversationId || `session_${Date.now()}`,
        async_execution: false, // For chat, we want immediate results
      };

      // Execute the tool
      const result = await this.executeTool(serverId, toolName, toolArguments);

      // Extract content from MCP response
      let responseContent = '';
      if (result && result.content) {
        for (const content of result.content) {
          if (typeof content === 'string') {
            responseContent += content;
          } else if (content.text) {
            responseContent += content.text;
          }
        }
      }

      return {
        success: true,
        content: responseContent || 'Tool executed successfully',
        toolName,
        serverId,
      };
    } catch (error) {
      console.error('MCP tool execution error:', error);
      return {
        success: false,
        content: `Error executing MCP tool: ${error.message}`,
        toolName,
        serverId,
        error: error.message,
      };
    }
  }
}

// MCP Client implementation for WebSocket connections
class MCPClient {
  constructor(config) {
    this.config = config;
    this.connected = false;
    this.websocket = null;
    this.tools = [];
    this.resources = [];
    this.pendingRequests = new Map();
    this.requestId = 0;
  }

  async connect() {
    return new Promise((resolve, reject) => {
      try {
        // Only attempt WebSocket connection if the server has a websocket_url configured
        if (!this.config.websocket_url) {
          console.log(`✅ Server ${this.config.id} uses HTTP/HTTPS only (no WebSocket needed)`);
          this.connected = true; // Mark as connected for HTTP-only servers
          resolve(true);
          return;
        }

        const wsUrl = this.config.websocket_url;
        console.log(`🔌 Attempting WebSocket connection to: ${wsUrl}`);
        this.websocket = new WebSocket(wsUrl);

        this.websocket.onopen = async () => {
          this.connected = true;
          try {
            await this.initialize();
            resolve(true);
          } catch (error) {
            reject(error);
          }
        };

        this.websocket.onerror = (error) => {
          console.error(`WebSocket connection failed for server ${this.config.id}:`, error);
          reject(new Error(`WebSocket connection failed: ${error.message || 'Unknown error'}`));
        };

        this.websocket.onclose = () => {
          this.connected = false;
          console.log(`WebSocket connection closed for server ${this.config.id}`);
        };

        this.websocket.onmessage = (event) => {
          this.handleMessage(JSON.parse(event.data));
        };

        // Timeout after 10 seconds
        setTimeout(() => {
          if (!this.connected) {
            reject(new Error('Connection timeout'));
          }
        }, 10000);
      } catch (error) {
        reject(error);
      }
    });
  }

  async disconnect() {
    if (this.websocket) {
      this.websocket.close();
      this.websocket = null;
    }
    this.connected = false;
  }

  async initialize() {
    const initRequest = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'initialize',
      params: {
        protocolVersion: '2024-11-05',
        capabilities: {
          tools: {},
          resources: {},
          prompts: {},
          logging: {},
        },
        clientInfo: {
          name: 'ChatUI-MCPClient',
          version: '1.0.0',
        },
      },
    };

    await this.sendRequest(initRequest);
  }

  async sendRequest(request) {
    if (!this.connected || !this.websocket) {
      throw new Error('Not connected to MCP server');
    }

    return new Promise((resolve, reject) => {
      this.pendingRequests.set(request.id, { resolve, reject });
      this.websocket.send(JSON.stringify(request));

      // Timeout after 30 seconds
      setTimeout(() => {
        if (this.pendingRequests.has(request.id)) {
          this.pendingRequests.delete(request.id);
          reject(new Error('Request timeout'));
        }
      }, 30000);
    });
  }

  handleMessage(message) {
    if (message.id && this.pendingRequests.has(message.id)) {
      const { resolve, reject } = this.pendingRequests.get(message.id);
      this.pendingRequests.delete(message.id);

      if (message.error) {
        reject(new Error(message.error.message || 'MCP error'));
      } else {
        resolve(message.result);
      }
    }
  }

  async listTools() {
    const request = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'tools/list',
      params: {},
    };

    const result = await this.sendRequest(request);
    this.tools = result.tools || [];
    return this.tools;
  }

  async listResources() {
    const request = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'resources/list',
      params: {},
    };

    const result = await this.sendRequest(request);
    this.resources = result.resources || [];
    return this.resources;
  }

  async executeTool(toolName, toolArguments) {
    const request = {
      jsonrpc: '2.0',
      id: this.generateId(),
      method: 'tools/call',
      params: {
        name: toolName,
        arguments: toolArguments,
      },
    };

    const result = await this.sendRequest(request);
    return result;
  }

  generateId() {
    return (++this.requestId).toString();
  }
}

// Create singleton instance
const mcpService = new MCPService();
export default mcpService;
